/**
 * LoadingButton — wraps a <button> with a `loading` prop that disables the
 * button + replaces its label with an inline spinner. Pairs with the
 * existing `.btn` / `.btn-primary` / `.btn-secondary` / `.btn-danger` CSS
 * classes and the existing @keyframes spin in app.css.
 *
 * Usage:
 *   <LoadingButton
 *     type="submit"
 *     className="btn btn-primary"
 *     loading={saving}
 *     loadingLabel="Saving…"
 *   >
 *     Save changes
 *   </LoadingButton>
 *
 * Why a primitive vs every callsite hand-rolling: the inline spinner +
 * disabled-while-loading pattern recurs across signup, save flows, OAuth
 * connect, invite send, etc. Hand-rolled versions drift on spinner size,
 * spinner color, "loading…" copy, and forgetting to disable. One primitive
 * locks the shape.
 *
 * Constraints respected:
 *   - No new CSS; reuses the `.spinner` rule already defined in app.css
 *     (14x14px, teal-on-translucent border, 0.7s linear spin keyframe).
 *   - Passes through every native <button> prop so callers can attach
 *     onClick, form, name, value, aria-*, etc.
 *   - When loading, swaps in `aria-busy="true"` so screen readers announce
 *     the in-flight state.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** When true, replaces children with a spinner + optional loadingLabel,
   *  disables the button, and sets aria-busy. */
  loading?: boolean;
  /** Optional text shown next to the spinner while loading. Default:
   *  no text (spinner only) so narrow icon-buttons don't reflow width. */
  loadingLabel?: string;
  /** Pixel size override of the spinner; if omitted the `.spinner` class
   *  default (14px) applies. */
  spinnerSize?: number;
  children: ReactNode;
}

export const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  function LoadingButton(
    { loading = false, loadingLabel, spinnerSize, disabled, children, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={rest.type ?? 'button'}
        {...rest}
        disabled={disabled === true || loading}
        aria-busy={loading || undefined}
      >
        {loading ? (
          <>
            <span
              className="spinner"
              aria-hidden="true"
              style={
                spinnerSize !== undefined
                  ? { width: spinnerSize, height: spinnerSize }
                  : undefined
              }
            />
            {loadingLabel !== undefined && loadingLabel !== '' ? (
              <span>{loadingLabel}</span>
            ) : null}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);
